import * as React from 'react';
import TradePage from '../../../common/components/TradePage/TradePage';
import config from '../../../store/mmd/config';
import { IRegistrationDetailsUIProps } from './RegistrationDetailsInterfaces';
import { Select } from '@makemydeal/ui-bricks/dist/cox';
import { SpinnerUI } from '@makemydeal/dr-activities-common';

const SELECT_VALUE = {
    EMPTY: '',
    YES: 'yes',
    NO: 'no'
} as const;

const SELECT_OPTIONS = [
    { value: SELECT_VALUE.EMPTY, label: 'Select', disabled: true },
    { value: SELECT_VALUE.YES, label: 'Yes' },
    { value: SELECT_VALUE.NO, label: 'No' }
];

const FIELD_NAME = {
    BUYER_REGISTERED_OWNER: 'isBuyerRegisteredOwner',
    SELLING_VEHICLE_SAME_STATE: 'isSellingVehicleRegisteredInSameState'
} as const;

const renderSelectOptions = () => {
    return SELECT_OPTIONS.map(({ value, label, disabled }) => (
        <option key={value} value={value} disabled={disabled}>
            {label}
        </option>
    ));
};

export const RegistrationDetailsUI: React.FC<IRegistrationDetailsUIProps> = (props) => {
    const {
        pageTitle,
        pageSubTitle,
        buyerRegisteredOwner,
        sellingVehicleRegisteredInSameState,
        stepperRoutes,
        goToRoute,
        dispatchAnalytics,
        updateBuyerRegisteredOwner,
        updateSellingVehicleRegisteredInSameState,
        fetchLienholders,
        onEstimateBalanceClick,
        goBack,
        removeTradeIn,
        isFetchingLienholders,
        cdlTradeInProgressStepperClicked,
        urlPath
    } = props;

    const getBuyerRegisteredOwnerValue = () => {
        if (buyerRegisteredOwner === undefined) {
            return SELECT_VALUE.EMPTY;
        }
        return buyerRegisteredOwner ? SELECT_VALUE.YES : SELECT_VALUE.NO;
    };

    const getSellingVehicleRegisteredInSameStateValue = () => {
        if (sellingVehicleRegisteredInSameState === undefined) {
            return SELECT_VALUE.EMPTY;
        }
        return sellingVehicleRegisteredInSameState ? SELECT_VALUE.YES : SELECT_VALUE.NO;
    };

    const isButtonDisabled = buyerRegisteredOwner === undefined || sellingVehicleRegisteredInSameState === undefined;

    const handleSelectOnChange: React.ChangeEventHandler<HTMLSelectElement> = (event): void => {
        const { name, value } = event.currentTarget;

        if (!value) {
            return;
        }

        const isTrue = value === SELECT_VALUE.YES;

        if (name === FIELD_NAME.BUYER_REGISTERED_OWNER) {
            updateBuyerRegisteredOwner(isTrue);
        } else if (name === FIELD_NAME.SELLING_VEHICLE_SAME_STATE) {
            updateSellingVehicleRegisteredInSameState(isTrue);
        }
    };

    return (
        <TradePage
            headerOnTop
            showCustomTitle
            pageClass="registration-details-page"
            pageTitle={pageTitle}
            pageSubTitle={pageSubTitle}
            goToRoute={goToRoute}
            dispatchAnalytics={dispatchAnalytics}
            footerProps={{
                showBackButton: true,
                onBackButtonClick: goBack,
                disableActionButton: false,
                fullWidthLayout: true,
                showSkipButton: true,
                skipButtonText: 'Cancel Trade-in',
                onSkipButtonClick: removeTradeIn
            }}
            stepperRoutes={stepperRoutes}
            showTradeErrorMessage={false}
            staticAssetsConfig={config.staticImages}
            stepLabelVisible={true}
            isStepperClickable={true}
            cdlPageProgressStepperClicked={cdlTradeInProgressStepperClicked}
            urlPath={urlPath}
        >
            <div className="registration-details-container">
                {isFetchingLienholders && (
                    <div className="spinner-overlay">
                        <SpinnerUI size="30" />
                    </div>
                )}
                <div className="registration-details-header">
                    <p className="title">Let's see how much you owe on your open auto loan</p>
                    <p className="subtitle">
                        Once we know the balance, we can tell you exactly how much you'll receive for your car. We calculate this by
                        subtracting the amount you still owe from its value.
                    </p>
                </div>
                <div className="row-responsive">
                    <div className="responsive">
                        <Select
                            selectId={FIELD_NAME.BUYER_REGISTERED_OWNER}
                            name={FIELD_NAME.BUYER_REGISTERED_OWNER}
                            value={getBuyerRegisteredOwnerValue()}
                            defaultValue=""
                            label="Are you the registered owner of this vehicle?"
                            onChangeFunction={handleSelectOnChange}
                        >
                            {renderSelectOptions()}
                        </Select>
                    </div>
                    <div className="responsive">
                        <Select
                            selectId={FIELD_NAME.SELLING_VEHICLE_SAME_STATE}
                            name={FIELD_NAME.SELLING_VEHICLE_SAME_STATE}
                            value={getSellingVehicleRegisteredInSameStateValue()}
                            defaultValue=""
                            label="Is this vehicle registered in the same state as your new vehicle will be registered in?"
                            onChangeFunction={handleSelectOnChange}
                        >
                            {renderSelectOptions()}
                        </Select>
                    </div>
                </div>

                <div className="find-my-loan-button-container">
                    <button
                        data-testid="find-my-loan-button"
                        className="btn btn-primary"
                        onClick={fetchLienholders}
                        disabled={isButtonDisabled}
                    >
                        Find My Open Auto Loan
                    </button>
                </div>

                <p className="estimate-loan-balance-link">
                    You can also{' '}
                    <a data-testid="estimate-loan-balance-link" className="primary-link" onClick={onEstimateBalanceClick}>
                        estimate your loan balance.{' '}
                    </a>
                    We'll confirm the exact amount before finalizing your purchase.
                </p>
            </div>
        </TradePage>
    );
};

export default RegistrationDetailsUI;
